import { supabase } from '../lib/supabaseClient';
import type {
  CreateStockCountLineInput,
  CreateTransferLineInput,
  ExpiryLot,
  StockCount,
  StockCountLine,
  Transfer,
  TransferLine,
  UUID,
  WasteEvent,
  WasteReasonCode,
} from '../types/domain';

/**
 * FASE 6 adapter: Stock Opname / Transfer / Waste / Expiry-Lot. Every write
 * calls a permission-gated RPC wrapper from migration 0033
 * (fase6_rbac_operationalization) — never a direct INSERT — exactly the same
 * pattern api/purchasing.ts and api/masterData.ts already use. Reads go
 * straight to PostgREST (`.from()`), relying on RLS for tenant isolation.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED for this browser-side adapter. The RPC
 * functions themselves were verified directly against alex-erp-dev with the
 * real dev-warehouse/dev-manager/dev-auditor/dev-purchasing identities
 * (happy path, double-post rejection, insufficient-stock rejection,
 * same-warehouse rejection, branch-scope denial, cross-tenant FK denial) —
 * see the 0033 migration commit for the exact test transcript. Only the
 * expiry-lot happy path is UNTESTED so far (no tracks_expiry item with a
 * POSTED goods receipt line exists yet in DEV data).
 */

// ---------------------------------------------------------------------------
// Stock Opname
// ---------------------------------------------------------------------------

export async function listStockCounts(branchId?: UUID): Promise<StockCount[]> {
  let query = supabase
    .from('stock_counts')
    .select('count_id, tenant_id, branch_id, warehouse_id, count_number, status, counted_by, posted_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listStockCounts failed: ${error.message}`);
  return (data ?? []) as StockCount[];
}

export async function listStockCountLines(countId: UUID): Promise<StockCountLine[]> {
  const { data, error } = await supabase
    .from('stock_count_lines')
    .select('count_line_id, tenant_id, count_id, item_id, uom_id, system_qty, counted_qty, variance_qty')
    .eq('count_id', countId);
  if (error) throw new Error(`listStockCountLines failed: ${error.message}`);
  return (data ?? []) as StockCountLine[];
}

export interface CreateStockCountInput {
  tenantId: UUID;
  branchId: UUID;
  warehouseId: UUID;
  lines: CreateStockCountLineInput[];
}

/** Calls fn_create_stock_count (migration 0033, permission-gated on stock_count.create, branch-scoped). */
export async function createStockCount(input: CreateStockCountInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_stock_count', {
    p_tenant_id: input.tenantId,
    p_branch_id: input.branchId,
    p_warehouse_id: input.warehouseId,
    p_lines: input.lines,
  });
  if (error) throw new Error(`fn_create_stock_count failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_post_stock_count_checked (migration 0033, permission-gated on stock_count.post) — writes ADJUSTMENT_IN/OUT to stock_ledger. */
export async function postStockCount(countId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_post_stock_count_checked', { p_count_id: countId });
  if (error) throw new Error(`fn_post_stock_count_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------

/** transfers has no branch_id column (only source/dest warehouse_id) — a
 * transfer belongs to a branch's view when EITHER side touches one of that
 * branch's warehouses (an outgoing OR incoming transfer is relevant to
 * that branch's stock). Pass BranchContext's activeBranchWarehouseIds. */
export async function listTransfers(branchWarehouseIds?: UUID[]): Promise<Transfer[]> {
  let query = supabase
    .from('transfers')
    .select('transfer_id, tenant_id, transfer_number, source_warehouse_id, dest_warehouse_id, status, requested_by, posted_at, created_at')
    .order('created_at', { ascending: false });
  if (branchWarehouseIds) {
    if (branchWarehouseIds.length === 0) return [];
    const ids = branchWarehouseIds.join(',');
    query = query.or(`source_warehouse_id.in.(${ids}),dest_warehouse_id.in.(${ids})`);
  }
  const { data, error } = await query;
  if (error) throw new Error(`listTransfers failed: ${error.message}`);
  return (data ?? []) as Transfer[];
}

export async function listTransferLines(transferId: UUID): Promise<TransferLine[]> {
  const { data, error } = await supabase
    .from('transfer_lines')
    .select('transfer_line_id, tenant_id, transfer_id, item_id, uom_id, qty')
    .eq('transfer_id', transferId);
  if (error) throw new Error(`listTransferLines failed: ${error.message}`);
  return (data ?? []) as TransferLine[];
}

export interface CreateTransferInput {
  tenantId: UUID;
  sourceWarehouseId: UUID;
  destWarehouseId: UUID;
  lines: CreateTransferLineInput[];
}

/** Calls fn_create_transfer (migration 0033, permission-gated on transfer.create, scoped to the source warehouse's branch). */
export async function createTransfer(input: CreateTransferInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_transfer', {
    p_tenant_id: input.tenantId,
    p_source_warehouse_id: input.sourceWarehouseId,
    p_dest_warehouse_id: input.destWarehouseId,
    p_lines: input.lines,
  });
  if (error) throw new Error(`fn_create_transfer failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_post_transfer_checked (migration 0033, permission-gated on transfer.post) — writes TRANSFER_OUT/IN to stock_ledger; rejects insufficient stock. */
export async function postTransfer(transferId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_post_transfer_checked', { p_transfer_id: transferId });
  if (error) throw new Error(`fn_post_transfer_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Waste
// ---------------------------------------------------------------------------

export async function listWasteEvents(branchId?: UUID): Promise<WasteEvent[]> {
  let query = supabase
    .from('waste_events')
    .select(
      'waste_id, tenant_id, branch_id, warehouse_id, waste_number, item_id, uom_id, qty, reason_code, reason_note, status, reported_by, posted_at, created_at, expiry_lot_id'
    )
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listWasteEvents failed: ${error.message}`);
  return (data ?? []) as WasteEvent[];
}

export interface ReportWasteInput {
  tenantId: UUID;
  branchId: UUID;
  warehouseId: UUID;
  itemId: UUID;
  uomId: UUID;
  qty: number;
  reasonCode: WasteReasonCode;
  reasonNote?: string | null;
}

/** Calls fn_report_waste (migration 0033, permission-gated on waste.create, branch-scoped). */
export async function reportWaste(input: ReportWasteInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_report_waste', {
    p_tenant_id: input.tenantId,
    p_branch_id: input.branchId,
    p_warehouse_id: input.warehouseId,
    p_item_id: input.itemId,
    p_uom_id: input.uomId,
    p_qty: input.qty,
    p_reason_code: input.reasonCode,
    p_reason_note: input.reasonNote ?? null,
  });
  if (error) throw new Error(`fn_report_waste failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_post_waste_checked (migration 0033, permission-gated on waste.post) — writes WASTE to stock_ledger; rejects insufficient stock. */
export async function postWaste(wasteId: UUID, expiryLotId?: UUID | null): Promise<void> {
  const { error } = await supabase.rpc('fn_post_waste_checked', { p_waste_id: wasteId, p_expiry_lot_id: expiryLotId ?? null });
  if (error) throw new Error(`fn_post_waste_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Expiry / Lot
// ---------------------------------------------------------------------------

export interface EligibleExpiryGrLine {
  gr_line_id: UUID;
  gr_id: UUID;
  gr_number: string;
  branch_id: UUID;
  warehouse_id: UUID;
  item_id: UUID;
  qty_received: number;
  qty_allocated: number;
  qty_remaining: number;
  posted_at: string;
}

/**
 * Calls fn_list_eligible_expiry_gr_lines (migration 0034) — the
 * authoritative source for the receive-lot picker. Returns only POSTED GR
 * lines for tracks_expiry items with qty_remaining > 0 (already-fully-
 * allocated lines never appear), tenant-scoped by RLS like every other
 * read in the app.
 */
export async function listEligibleExpiryGrLines(): Promise<EligibleExpiryGrLine[]> {
  const { data, error } = await supabase.rpc('fn_list_eligible_expiry_gr_lines');
  if (error) throw new Error(`fn_list_eligible_expiry_gr_lines failed: ${error.message}`);
  return (data ?? []) as EligibleExpiryGrLine[];
}

export async function listExpiryLots(branchId?: UUID): Promise<ExpiryLot[]> {
  let query = supabase
    .from('expiry_lots')
    .select(
      'lot_id, tenant_id, branch_id, warehouse_id, item_id, uom_id, lot_no, expiry_date, qty_received, qty_remaining, status, source_gr_id, source_gr_line_id, created_at'
    )
    .order('expiry_date', { ascending: true });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listExpiryLots failed: ${error.message}`);
  return (data ?? []) as ExpiryLot[];
}

/** Calls fn_receive_expiry_lot_checked (migration 0033, permission-gated on expiry_lot.create) — requires the GR line's goods receipt to already be POSTED. */
export async function receiveExpiryLot(grLineId: UUID, lotNo: string, expiryDate: string, qty: number): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_receive_expiry_lot_checked', {
    p_gr_line_id: grLineId,
    p_lot_no: lotNo,
    p_expiry_date: expiryDate,
    p_qty: qty,
  });
  if (error) throw new Error(`fn_receive_expiry_lot_checked failed: ${error.message}`);
  return data as UUID;
}
